import { captureException, withErrorBoundary } from '@sentry/react'
import { Fragment, useCallback, useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router'

import ModalContainer from 'src/components/common/modal'
import OrganizationContext from 'src/context/organizationContext'
import { APP_ENV } from 'src/env'
import { handlePromise } from 'src/utils/handlePromise'
import { useOrnaBackendClient } from 'src/utils/hooks/useOrnaBackendClient'

import SentryFallback from '../SentryFallback'
import openNotification from '../notification'
import Spinner from '../spinner'

//TODO: Save the current user's org number within context and donot allow user to switch
// orgs if organizations.length < 1
const SwitchOrganizationModal = ({ showModal, closeModal }) => {
    const ornaBackendClient = useOrnaBackendClient()

    const history = useHistory()

    const [organizations, setOrganizations] = useState(null)
    const { setOrganizationId, organizationId } =
        useContext(OrganizationContext)

    const authorizeUser = useCallback(async () => {
        const [requestError, response] = await handlePromise(
            ornaBackendClient.get(
                APP_ENV.REACT_APP_ORNA_BACKEND_URL + '/authorize-user',
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )
        )

        if (requestError) {
            captureException(requestError, {
                extra: {
                    message: {
                        content:
                            "An error occurred while fetching user's organization list",
                    },
                },
            })
            openNotification(
                'error',
                "Whoops we didn't expect this. Please try again",
                3
            )
        }

        const { organizations } = response.data

        if (!Array.isArray(organizations)) {
            const errMessage = `Expected array but received ${typeof organizations} as response for user's organization list`
            const error = new Error(errMessage)
            captureException(error)
            closeModal()
            return
        }

        if (organizations?.length > 1) {
            setOrganizations(organizations)
        } else if (organizations.length === 1) {
            setOrganizationId(organizations[0].organizationId)
            closeModal()
        }
    }, [closeModal, ornaBackendClient, setOrganizationId])

    useEffect(() => {
        if (showModal === true) {
            authorizeUser()
        }
    }, [showModal, authorizeUser])

    const handleOnClickOrganization = useCallback(
        (event, orgId) => {
            event.stopPropagation()
            setOrganizationId(orgId)
            history.replace('/')
            closeModal()
        },
        [closeModal, history, setOrganizationId]
    )

    const renderOrgSelector = () => {
        return (
            <div
                className="h-auto w-full transition-opacity duration-300 ease-in"
                style={{ opacity: organizations !== null ? 1 : 0 }}
            >
                <div className="w-full rounded-md bg-neutral-gray-400 px-4 py-4">
                    <div className="text-lg">Select organization</div>
                    <div className="">
                        {organizations?.length > 0
                            ? organizations.map((item, index) => (
                                  <Fragment key={index}>
                                      <button
                                          className={[
                                              'mt-4 flex w-full items-center rounded-md bg-neutral-gray-500 px-2 py-2 shadow-lg hover:bg-primary-red-500',
                                              `${item.organizationId === organizationId && 'cursor-not-allowed bg-primary-red-500'}`,
                                          ].join(' ')}
                                          onClick={(e) => {
                                              handleOnClickOrganization(
                                                  e,
                                                  item.organizationId
                                              )
                                          }}
                                          disabled={
                                              item.organizationId ===
                                              organizationId
                                          }
                                      >
                                          {item.organization.attachments
                                              .iconLogo ? (
                                              <img
                                                  className="rounded-50-percent h-10 w-10"
                                                  src={
                                                      item.organization
                                                          .attachments.iconLogo
                                                  }
                                              />
                                          ) : (
                                              <div className="rounded-50-percent h-10 w-10 bg-neutral-gray-75"></div>
                                          )}
                                          <div className="pl-4 text-white">
                                              {item.organization.legalName}
                                          </div>
                                      </button>
                                  </Fragment>
                              ))
                            : null}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <>
            <ModalContainer
                maskClosable={true}
                bodyStyle={{ background: '#2F3B52' }}
                visible={showModal}
                width={'600px'}
                closeModal={closeModal}
                closable={true}
            >
                {organizations === null ? <Spinner /> : renderOrgSelector()}
            </ModalContainer>
        </>
    )
}

export default withErrorBoundary(SwitchOrganizationModal, {
    fallback: <SentryFallback />,
})
